// The safety pass. Backward max-deadline Dijkstra computing, for every node,
// the latest local time you may DEPART it and still reach an end node before
// every serving lift shuts and before dark. Trail times are ability- and
// group-scaled, identical to the forward search, so a slow group's plan can
// never be certified by optimistic base times. See plan section 7.2.

import type { PlannerEdge, Difficulty } from "./graph-types";
import { planaiTrailWindow } from "./operations/planai";
import { DIFFICULTY_RANK, type DerivedPreferences } from "./preferences";
import {
  isEdgeTraversable,
  type EngineInputs,
  type EngineOptions,
} from "./engine-types";

/**
 * Whether an edge is permissible for THIS skier: lift status / bus opt-in, and
 * for trails the difficulty must be within reach. The home route uses the
 * mode's max rank (push-budget slack is a forward-search concern only), so a
 * node with no skiable-enough descent is never certified as safe. This is what
 * stops a beginner being routed home down a red.
 */
export function edgeAllowed(
  edge: PlannerEdge,
  inputs: EngineInputs,
  opts: EngineOptions,
  derived: DerivedPreferences,
): boolean {
  if (!isEdgeTraversable(edge, inputs, opts)) return false;
  if (edge.kind === "trail") {
    // A lift-only MTB rider never pedals up, so climb edges are not usable at
    // all when the per-day climb budget is zero. The safety pass must know this
    // so it never certifies a route home that relies on a pedal-up. The forward
    // search enforces the metres budget per plan (see itinerary.ts); here we
    // only gate the degenerate lift-only case. Ski edges never set `climb`, so
    // this branch is naturally MTB-only and the ski path is untouched.
    if (derived.sport === "mtb" && edge.climb && derived.climbBudgetM <= 0) {
      return false;
    }
    const rank = DIFFICULTY_RANK[edge.difficulty as Difficulty] ?? 2;
    if (rank > derived.maxRank) return false;
  }
  return true;
}

const HOME_BUFFER_HOURS = 10 / 60; // never cut the last lift finer than 10 min

function flatLiftQueueMinutes(
  edge: Extract<PlannerEdge, { kind: "lift" }>,
  inputs: EngineInputs,
): number {
  const info = inputs.liftInfo.get(edge.id);
  let queue = 5;
  if (inputs.crowdFactor > 1.3) {
    queue = 10;
    if ((info?.capacityPerHour ?? Infinity) < 1500) queue += 5;
  }
  return queue;
}

export function liftWaitMinutesAtHour(
  edge: Extract<PlannerEdge, { kind: "lift" }>,
  inputs: EngineInputs,
  hour: number,
): number {
  return inputs.waitMinutesByLiftHour?.get(edge.id)?.get(Math.max(8, Math.min(18, Math.round(hour))))
    ?? flatLiftQueueMinutes(edge, inputs);
}

/** Minutes to traverse an edge, ability/group-scaled for trails. */
export function legTimeMinutes(
  edge: PlannerEdge,
  inputs: EngineInputs,
  derived: DerivedPreferences,
): number {
  switch (edge.kind) {
    case "trail":
      // An MTB pedal-up edge is effort-bound, not gravity-fed: its time scales
      // by the rider's climb factor (fitness x bike type, or a flat e-bike
      // assist), NOT the descent ability/group factor. Descent trail edges keep
      // the existing `timeFactor` scaling. Ski edges never set `climb`, so the
      // ski path is byte-identical.
      if (edge.climb) return edge.baseMinutes * derived.climbFactor;
      return edge.baseMinutes * derived.timeFactor;
    case "lift": {
      const waits = [...(inputs.waitMinutesByLiftHour?.get(edge.id)?.values() ?? [])].filter(Number.isFinite);
      return edge.durationS / 60 + Math.max(flatLiftQueueMinutes(edge, inputs), ...waits);
    }
    case "link":
      return edge.durationS / 60;
    case "bus":
      return edge.durationS / 60; // already includes half-headway wait
  }
}

/** Direction-specific boarding windows. A later download never grants a
 * later ascent on the same lift identity. */
export function liftOperatingWindows(edge: Extract<PlannerEdge, { kind: "lift" }>, inputs: EngineInputs): { open: number; close: number }[] | null {
  const info = inputs.liftInfo.get(edge.id);
  if (!info) return null;
  return (edge.direction === "down" ? info.downloadOperatingWindows : undefined)
    ?? info.operatingWindows ?? [{ open: info.openHour, close: info.closeHour }];
}

/** Ordinary trails retain their existing dusk rule. A version-two park trail
 * must finish within its independently validated operator calendar. */
export function edgeArrivalDeadlineHour(edge: PlannerEdge, inputs: EngineInputs): number {
  return edge.kind === "trail" && edge.conditionalAccess === "bike-park-pass-v2"
    ? planaiTrailWindow(edge.bikeParkPass, inputs.planningDate, inputs.trailInfo.get(edge.id)?.osmId)?.close ?? -Infinity
    : Infinity;
}

export function edgeTravelMinutesAtHour(
  edge: PlannerEdge,
  inputs: EngineInputs,
  derived: DerivedPreferences,
  hour: number,
): number {
  if (edge.kind === "lift") {
    const wait = liftWaitMinutesAtHour(edge, inputs, hour);
    const windows = liftOperatingWindows(edge, inputs);
    if (windows && !windows.some(w => hour >= w.open && hour + wait / 60 <= w.close + 1e-9)) return Infinity;
    return edge.durationS / 60 + wait;
  }
  const minutes = legTimeMinutes(edge, inputs, derived);
  if (edge.kind === "trail" && edge.conditionalAccess === "bike-park-pass-v2") {
    const window = planaiTrailWindow(edge.bikeParkPass, inputs.planningDate, inputs.trailInfo.get(edge.id)?.osmId);
    if (!window || hour < window.open || hour + minutes / 60 > window.close + 1e-9) return Infinity;
  }
  return minutes;
}

/**
 * Latest local hour you may DEPART the from-node on this edge: the lift's
 * close time, dusk for a descent, or the bus's last departure, capped at the
 * day's end.
 */
export function edgeDeadlineHour(
  edge: PlannerEdge,
  inputs: EngineInputs,
  endHour: number,
): number {
  let raw: number;
  switch (edge.kind) {
    case "lift":
      raw = liftOperatingWindows(edge, inputs)?.at(-1)?.close ?? endHour;
      break;
    case "trail":
      raw = Math.min(inputs.sunsetHour ?? endHour, edgeArrivalDeadlineHour(edge, inputs));
      break;
    case "bus":
      raw = edge.lastDepartureHour;
      break;
    case "link":
      raw = endHour;
      break;
  }
  return Math.min(raw, endHour);
}

export function edgeEarliestHour(
  edge: PlannerEdge,
  inputs: EngineInputs,
  fallbackStartHour: number,
): number {
  if (edge.kind === "trail" && edge.conditionalAccess === "bike-park-pass-v2") {
    const window = planaiTrailWindow(edge.bikeParkPass, inputs.planningDate, inputs.trailInfo.get(edge.id)?.osmId);
    return window && fallbackStartHour < window.close ? Math.max(window.open, fallbackStartHour) : Infinity;
  }
  if (edge.kind !== "lift") return fallbackStartHour;
  const info = inputs.liftInfo.get(edge.id);
  if (!info) return fallbackStartHour;
  const windows = liftOperatingWindows(edge, inputs)!;
  for (const window of windows) {
    const depart = Math.max(window.open, fallbackStartHour);
    if (depart + liftWaitMinutesAtHour(edge, inputs, depart) / 60 <= window.close + 1e-9) return depart;
  }
  return Infinity;
}

export type LatestDeparture = {
  /** node id -> latest local hour you may depart it and still get home. */
  latest: Map<string, number>;
  /** node id -> the edge to take to follow the latest-feasible route home. */
  nextHop: Map<string, PlannerEdge>;
};

/**
 * Backward pass from the end node(s). Max-heap settle-once: edge deadlines
 * only tighten with distance from home, so the first time a node is popped it
 * holds its true latest departure.
 */
export function computeLatestDeparture(
  inputs: EngineInputs,
  derived: DerivedPreferences,
  opts: EngineOptions,
  endNodeIds: string[],
  endHour: number,
): LatestDeparture {
  const latest = new Map<string, number>();
  const nextHop = new Map<string, PlannerEdge>();

  // Incoming adjacency: for relaxing u via edge u->v we index edges by `to`.
  const incoming = new Map<string, PlannerEdge[]>();
  for (const edge of inputs.graph.edges) {
    if (!edgeAllowed(edge, inputs, opts, derived)) continue;
    if (
      edge.kind === "lift" &&
      edgeEarliestHour(edge, inputs, -Infinity) >=
        edgeDeadlineHour(edge, inputs, endHour)
    ) {
      continue;
    }
    const list = incoming.get(edge.to);
    if (list) list.push(edge);
    else incoming.set(edge.to, [edge]);
  }

  // Simple array-based max selection (graphs are small: hundreds of nodes).
  const settled = new Set<string>();
  for (const id of endNodeIds) latest.set(id, endHour);

  while (true) {
    // Pop the unsettled node with the greatest latest-departure.
    let bestNode: string | null = null;
    let bestVal = -Infinity;
    for (const [node, val] of latest) {
      if (settled.has(node)) continue;
      if (val > bestVal) {
        bestVal = val;
        bestNode = node;
      }
    }
    if (bestNode === null) break;
    settled.add(bestNode);

    for (const edge of incoming.get(bestNode) ?? []) {
      const u = edge.from;
      if (settled.has(u)) continue;
      const travelH = legTimeMinutes(edge, inputs, derived) / 60;
      const deadline = edgeDeadlineHour(edge, inputs, endHour);
      let latestDepart = Math.min(
        deadline,
        bestVal - travelH,
        edgeArrivalDeadlineHour(edge, inputs) - travelH,
      );
      if (edge.kind === "lift") {
        const windows = liftOperatingWindows(edge, inputs) ?? [{ open: -Infinity, close: endHour }];
        const queueHours = travelH - edge.durationS / 3600;
        latestDepart = Math.max(-Infinity, ...windows.map(window => {
          const candidate = Math.min(latestDepart, window.close - queueHours);
          return candidate >= window.open ? candidate : -Infinity;
        }));
      }
      if (edge.kind === "lift" && latestDepart < edgeEarliestHour(edge, inputs, -Infinity)) {
        continue;
      }
      if (edge.kind === "trail" && edge.conditionalAccess === "bike-park-pass-v2" && latestDepart < edgeEarliestHour(edge, inputs, -Infinity)) continue;
      const candidate = latestDepart;
      if (candidate > (latest.get(u) ?? -Infinity)) {
        latest.set(u, candidate);
        nextHop.set(u, edge);
      }
    }
  }

  return { latest, nextHop };
}

export type HomeLeg = {
  edge: PlannerEdge;
  departHour: number;
  arriveHour: number;
};

/**
 * Follow the latest-feasible route home from a node at a given clock time.
 * Returns null if home cannot be reached in time (the caller should not have
 * entered such a state, but we verify rather than trust).
 */
export function routeHome(
  startNode: string,
  clockHour: number,
  endNodeIds: Set<string>,
  ld: LatestDeparture,
  inputs: EngineInputs,
  derived: DerivedPreferences,
  endHour: number,
): HomeLeg[] | null {
  const legs: HomeLeg[] = [];
  let node = startNode;
  let clock = clockHour;
  let guard = 0;

  while (!endNodeIds.has(node)) {
    if (guard++ > 200) return null; // cycle backstop
    const edge = ld.nextHop.get(node);
    if (!edge) return null;
    const deadline = edgeDeadlineHour(edge, inputs, endHour);
    const depart = Math.max(clock, edgeEarliestHour(edge, inputs, clock));
    if (depart > deadline + 1e-9) return null; // missed the window
    const travelH = edgeTravelMinutesAtHour(edge, inputs, derived, depart) / 60;
    const arrive = depart + travelH;
    if (!Number.isFinite(arrive) || arrive > endHour + 1e-9) return null;
    legs.push({ edge, departHour: depart, arriveHour: arrive });
    clock = arrive;
    node = edge.to;
  }
  return legs;
}

/**
 * Forward-search guard: may we take this edge from the current node at the
 * current clock and still be certain of getting home? Requires the edge's own
 * window to be open AND the arrival node to retain a feasible departure.
 */
export function edgePassesGuard(
  edge: PlannerEdge,
  clockHour: number,
  ld: LatestDeparture,
  inputs: EngineInputs,
  derived: DerivedPreferences,
  endHour: number,
): boolean {
  const depart = Math.max(clockHour, edgeEarliestHour(edge, inputs, clockHour));
  if (depart > edgeDeadlineHour(edge, inputs, endHour) + 1e-9) return false;
  const arrive = depart + edgeTravelMinutesAtHour(edge, inputs, derived, depart) / 60;
  const latestAtV = ld.latest.get(edge.to);
  if (latestAtV === undefined) return false; // v cannot reach home at all
  return arrive <= latestAtV - HOME_BUFFER_HOURS + 1e-9;
}
